import { HttpError, useApiUrl } from "@refinedev/core";
import React from "react";
import { SmlAsset, JobMeta, SmlJsonQuery } from "src/types/intelligence-search";
import { useSmartCustomQuery } from "@src/hooks/use-smart-custom-query";

export type DryRunResponse = {
    query: string;
    meta: JobMeta;
    cache_hit: boolean;
    schema: any[];
    ref_assets: SmlAsset[];
};

export type DryRunErrorResponse = HttpError & {
    errors: string[];
    suggestions: string[];
};

export const useDryRun = ({
    productCode,
    jsonQuery,
    dataProviderName = "semantic",
    shouldFetchData = true,
    queryKey,
    startDate,
    endDate,
}: {
    queryKey?: string;
    productCode?: string;
    jsonQuery?: SmlJsonQuery;
    dataProviderName?: string;
    shouldFetchData?: boolean;
    startDate?: string;
    endDate?: string;
}) => {
    const [error, setError] = React.useState<{
        statusCode: number;
        message: string[];
    } | null>(null);
    const [jsonQueryState, setJsonQueryState] = React.useState<SmlJsonQuery | undefined>(jsonQuery);
    const apiUrl = useApiUrl(dataProviderName);
    const encodedJsonQuery = jsonQueryState ? btoa(JSON.stringify(jsonQueryState)) : undefined;

    const { debouncedRefetch, data, isError, isLoading } = useSmartCustomQuery({
        url: `${apiUrl}/dry-run`,
        method: "GET",
        config: {
            headers: {
                "product-code": productCode,
                "start-date": startDate,
                "end-date": endDate,
            },
            params: {
                encoded_json_query: encodedJsonQuery,
            },
        },
        queryOptions: {
            queryKey: ["dry-run", queryKey],
            enabled: false,
            onSuccess: () => {
                setError(null);
            },
            onError: (error) => {
                const errorMessage = error.response?.data?.detail?.errors ?? error.response?.data?.message ?? [];
                setError({
                    statusCode: error.response?.status,
                    message: Array.isArray(errorMessage) ? errorMessage : [errorMessage],
                });
            },
        },
    });

    React.useEffect(() => {
        setJsonQueryState(jsonQuery);
    }, [JSON.stringify(jsonQuery)]);

    return {
        isLoading,
        isError,
        data: data,
        error,
        refetch: debouncedRefetch,
    };
};
